import type { GetOrganizationMetadataQuery } from '@pypestream/api-services/urql';

/**
 * Per-org `canManageAccess` resolution (PSTUDIO-3111).
 *
 * The backend is the source of truth for authorization: each org's
 * `canManageAccess` comes from `organizationMetadata(accountId).canManageAccess`.
 * These helpers only normalise and aggregate those backend responses — they do
 * NOT compute permissions on the client. Kept dependency-light (type-only import)
 * so unit tests do not pull in the design-system runtime.
 */
export type OrganizationMetadataResult = {
  accountId: string;
  canManageAccess: boolean;
};

export const toOrganizationMetadataResult = (
  accountId: string,
  data: GetOrganizationMetadataQuery | undefined
): OrganizationMetadataResult => ({
  accountId,
  canManageAccess: !!data?.admin_?.organizationMetadata?.canManageAccess,
});

export const buildCanManageAccessByOrgId = (
  results: OrganizationMetadataResult[]
): Record<string, boolean> =>
  results.reduce<Record<string, boolean>>(
    (acc, { accountId, canManageAccess }) => {
      if (accountId) {
        acc[accountId] = canManageAccess;
      }
      return acc;
    },
    {}
  );

// NOTE: one organizationMetadata query is issued per org (N-query fan-out),
// because the backend exposes canManageAccess only per single accountId. For
// users in many child orgs this is N parallel requests per page load; a
// batched backend field (organizationMetadata(accountIds: [...])) would reduce
// it to one request and is the right follow-up. Failures are isolated below.
export const fetchCanManageAccessByOrgId = async (
  orgs: { id?: string | null }[],
  getOrganizationMetadata: (vars: {
    accountId: string;
  }) => Promise<{ data?: GetOrganizationMetadataQuery }>
): Promise<Record<string, boolean>> => {
  const accountIds = orgs
    .map((org) => org.id)
    .filter((id): id is string => !!id);

  // Promise.allSettled (not all): a single failed org-metadata query must not
  // break the whole page. A rejected lookup falls back to canManageAccess:false
  // (fail-closed — deny, never grant, on error).
  const settled = await Promise.allSettled(
    accountIds.map((accountId) =>
      getOrganizationMetadata({ accountId }).then((res) =>
        toOrganizationMetadataResult(accountId, res.data)
      )
    )
  );

  const results = settled.map((outcome, index) =>
    outcome.status === 'fulfilled'
      ? outcome.value
      : { accountId: accountIds[index], canManageAccess: false }
  );

  return buildCanManageAccessByOrgId(results);
};

/**
 * Per-org authorization lookup used by the user-details mutation guards.
 * Returns the backend `canManageAccess` for a single org from the rows already
 * resolved into the viewed user's state. Fail-closed: unknown org / missing
 * flag → false. Pure (no design-system import) so it is unit-testable.
 */
export const getOrgCanManageAccess = (
  orgs:
    | ReadonlyArray<{ id?: string | null; canManageAccess?: boolean }>
    | undefined,
  orgId: string | null | undefined
): boolean =>
  !!orgId && !!orgs?.find((org) => org.id === orgId)?.canManageAccess;
